import { ChangeDetectorRef, Component, DestroyRef, ElementRef, Input, inject, input, signal } from '@angular/core';
import { EntryData, ProviderData, Structure } from '../structure-data.models';
import { TedDomain } from '../structure-data.models';
import { UtilService } from 'src/app/util.service';
import { CommonService } from 'src/app/common.service';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { ConfigService } from 'src/app/config.service';
import { PlatformService } from 'src/app/platform.service';
import { MolstarPluginService } from 'src/app/molstar-plugin-service';
import { applyAlphaMissenseTooltips, colorByAlphaMissense,resetColoring, resetTooltips } from 'src/app/molstar-alpha-missense';
import { colorByTed } from '../../molstar-alpha-ted';
import { DownloadOption } from '../entry-dropdown/entry-dropdown.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { EntrySelectors } from 'src/app/state/entry.selectors';
import { EntryService } from '../entry.service';
import { combineLatest, filter, map, take } from 'rxjs';
export interface MappedTedDomain {
  start: number;
  end: number;
  label: string;
  color: string;
  width: number;
  tedNumber: number;
  plddt: number;
  boundaries?: string;
  length?: number;
  qscore: number;
  cath_assignment_level: string;
  quality: string
}
@Component({
  selector: 'domains-tab',
  templateUrl: './domains-tab.component.html',
  styleUrl: './domains-tab.component.css',
  standalone: false
})

export class DomainsTabComponent {
  private store = inject(Store);
  public accession = toSignal(this.store.select(EntrySelectors.accession));
  public uniprotAccession:string = "";
  public entryData = signal<EntryData>({} as EntryData);
  public tedDomainData = signal<TedDomain[]>([]);
  public csvAmData = signal<string>('');
  public uniprotData = signal<any>({});
  public providerData = signal<ProviderData>({} as ProviderData);
  
  public isTedDomain = signal<boolean>(false);
  private readonly destroyRef = inject(DestroyRef);
  public isCopySequence: boolean = false;
  public pdbeMolstar: any;
  public structure: Structure;
  public lastUpdateDate: string;
  public molLoadComplete: boolean = false;
  public rangeFilter: any;
  public activeDomain = signal<MappedTedDomain>(null);
  public mappedTedDomainData = signal<MappedTedDomain[]>([]);
  public tedDomainNumberForClickActive = signal<number | null>(null);

  public dropdownSelected: string;
  public dropdownOptions: DownloadOption[] = [];
  public isOldUniprotEntry: boolean = false;

  constructor(
    private utilService: UtilService,
    public commonService: CommonService,
    private gaService: GoogleAnalyticsService,
    private configService: ConfigService,
    private platform: PlatformService,
    private el: ElementRef,
    private molstarPluginService : MolstarPluginService,
    private changeDetectorRef: ChangeDetectorRef,
    public entryService: EntryService,
  ) {}
  public readonly isSidebarDisplayed = signal<boolean>(true);
  public isAlphaMis: boolean = false;
  public isModalConf: boolean = true;
  public highlightedSequence: { text: string; highlight: boolean }[] = [];

  ngOnInit(): void {
    
    combineLatest([
      this.store.select(EntrySelectors.entryData).pipe(),
      this.store.select(EntrySelectors.tedDomainData).pipe(),
      this.store.select(EntrySelectors.csvAmData).pipe(),
      this.store.select(EntrySelectors.uniprotData).pipe(),
      this.store.select(EntrySelectors.providerData).pipe(),
    ])
      .pipe(

        filter(([entryData, tedDomainData, csvAmData, uniprotData, providerData]) =>
          !!entryData &&
          !!entryData.modelEntityId &&   // prove entry is loaded
          Array.isArray(tedDomainData) &&
          tedDomainData.length > 0 &&
          !!tedDomainData[0].ted_domain_no // prove ted domain is loaded
        ),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([entryData, tedDomainData, csvAmData, uniprotData, providerData]) => {
        this.entryData.set(entryData)
        this.tedDomainData.set(tedDomainData)
        this.csvAmData.set(csvAmData)
        this.uniprotData.set(uniprotData);
        this.providerData.set(providerData);
        this.uniprotAccession = this.entryData().uniprotAccession;
        // if (this.providerData().providerName === 'Google DeepMind' && entryData.isUniProt) {
        //   if((this.entryData().sequenceVersionDate!= "" || this.entryData().sequenceVersionDate!= null) && (this.uniprotData().sequence.modified != "" || this.uniprotData().sequence.modified != null)){
        //     this.isOldUniprotEntry = this.utilService.areDatesDifferent(this.entryData().sequenceVersionDate, this.uniprotData().sequence.modified);
        //   }
        // }
        this.setInitialTedDomain();
        this.initMolstar();
      });
     
  }
  ngOnChanges(): void {
    this.updateDropdown()
  }

  setInitialTedDomain(){
    if (this.tedDomainData()) {
      const tedDomainData = [];
      const seqLength = this.entryData().sequenceEnd;
      this.tedDomainData().forEach((item, index) => {
        for (let seg of item.segments) {
          tedDomainData.push({
            start: seg.af_start,
            end: seg.af_end,
            label: item.cath_label,
            qscore: item.qscore,
            cath_assignment_level: item.cath_assignment_level,
            tedNumber: item.ted_domain_no,
            plddt: item.plddt,
            quality: this.getQuality(item.qscore, item.cath_assignment_level),
            color: this.utilService.fetchColorByIndex(index),
            // width: this.getResWidth(seg.af_start, seg.af_end, this.lengthOfProtein(), 100 ),
            // width: (seg.af_end-seg.af_start+1) / this.lengthOfProtein()* 320 ,
            left: (seg.af_start-1) / seqLength* 100 ,
            right: (seqLength-seg.af_end) / seqLength* 100 ,
          });
        }
      });
      const sortedTedDomainData = tedDomainData.sort((a, b) => {
        if (a.start === b.start) {
          return a.end - b.end;
        }
        return a.start - b.start;
      });
      const mappedTedDomainDataWithGap = sortedTedDomainData.map(
        (item, index) => {
          return {
            ...item,
            gap:
              index !== sortedTedDomainData.length - 1
                ? ((sortedTedDomainData[index + 1].start -
                    sortedTedDomainData[index].end) /
                    seqLength) *
                  262
                : 0,
          };
        }
      );
      
      this.mappedTedDomainData.update(() => mappedTedDomainDataWithGap);
      this.activeDomain.set(this.mappedTedDomainData()[0]);
      const similarTeds = this.mappedTedDomainData().filter(
        (item) => item.tedNumber === this.activeDomain().tedNumber
      );
      const boundaries = this.getBoundaries(similarTeds);
      const length = this.getTotalLength(similarTeds);
      this.activeDomain.set({ ...this.mappedTedDomainData()[0], boundaries, length });
      this.tedDomainNumberForClickActive.set(this.activeDomain().tedNumber);
      this.entryService.setActiveDomain(this.activeDomain())
      this.updateDropdown() 
      this.processSequence();
    }
  }

  processSequence() {
    let sequence = this.entryData().sequence;
  
    const boundariesStr = this.activeDomain()?.boundaries; // e.g. "77-309, 503-741"
  
    this.highlightedSequence = [];
  
    if (!boundariesStr) {
      this.highlightedSequence.push({ text: sequence, highlight: false });
      return;
    }
  
    const ranges = boundariesStr.split(',').map(range => {
      const [start, end] = range.trim().split('-').map(Number);
      return { start, end };
    });
  
    let currentIndex = 1;
    for (const { start, end } of ranges) {
      if (currentIndex < start) {
        this.highlightedSequence.push({
          text: sequence.slice(currentIndex - 1, start - 1),
          highlight: false
        });
      }
      this.highlightedSequence.push({
        text: sequence.slice(start - 1, end),
        highlight: true
      });
      currentIndex = end + 1;
    }
  
    if (currentIndex <= sequence.length) {
      this.highlightedSequence.push({
        text: sequence.slice(currentIndex - 1),
        highlight: false
      });
    }
  }
  
  updateDropdown()
  {
    this.dropdownOptions = [
      {
        name: this.activeDomain() ? `Domain ${this.activeDomain().tedNumber}` : 'Active Domain',
        url: '',
        downloadable: false,
      },
      {
        name: `Model Confidence - pLDDT`,
        url: '',
        downloadable: false,
      }
      
    ];
    this.dropdownSelected = `Domain ${this.activeDomain().tedNumber}`;
  }

  toggleUpdateFlags(data) {
    this.isAlphaMis = data.isAlphaMis;
    this.isModalConf = data.isModalConf;
  }

  public async onDropdownSelect(event: string) {
    this.dropdownSelected = event;
    let tag = "";
    let gtagmsg = "";
    if(event.includes('Model Confidence')){
      this.applyMolstarColoring('plddt');
      tag = "colour_by_plddt";
      gtagmsg = "Clicks on colour by dropdown and select plddt";
    }else{
      tag = "colour_by_domain";
      gtagmsg = "Clicks on colour by dropdown and select domain";
      this.applyMolstarColoring('ted');
      const tedno = this.utilService.extractDomainNumber(event);
      this.tedDomainNumberForClickActive.set(tedno);
      const similarTeds = this.mappedTedDomainData().filter(
        (item) => item.tedNumber === tedno
      );
     
      const data = [
        ...similarTeds.map((domain) => {
          return {
            struct_asym_id: 'A',
            start_residue_number: domain.start,
            end_residue_number: domain.end,
            // focus: true,
            // color: domain.color,
          }
        }),
      ];
      await this.pdbeMolstar.visual.focus(data);
    }
    this.processSequence();
    this.gaService.eventEmitter('domain_colour_3D', 'colour_3D', 'clicks', tag, gtagmsg);
    
  }

  public async handleSelectedCard(domain){
    this.applyMolstarColoring('ted');
    this.utilService.clickDomainTrack('active');
    const similarTeds = this.mappedTedDomainData().filter(
      (item) => item.tedNumber === domain.tedNumber
    );
    const boundaries = this.getBoundaries(similarTeds);
    const length = this.getTotalLength(similarTeds);
    const quality = this.getQuality(domain.qscore, domain.cath_assignment_level);
    const qscore = domain.qscore;
    const cath_assignment_level = domain.cath_assignment_level;
    const data = [
      ...similarTeds.map((domain) => ({
        struct_asym_id: 'A',
        start_residue_number: domain.start,
        end_residue_number: domain.end,
        focus: true,
        color: domain.color,
      })),
    ];
    await this.pdbeMolstar.visual.focus(data);

    this.activeDomain.set({ ...domain, boundaries, length, qscore, cath_assignment_level, quality });
    this.tedDomainNumberForClickActive.set(this.activeDomain().tedNumber);
    this.updateDropdown()
    this.processSequence();
    this.gaService.eventEmitter('domain_select', 'domain_interaction', 'click', 'domain_selected', 'Clicks on domains for selection in the left column');
    this.changeDetectorRef.detectChanges();

  }

  public toggleSidebar() {
    this.isSidebarDisplayed.update((prev) => !prev);
  }
  public copySequence(sequence) {
    const text = `${sequence}`;
    this.utilService.copy(text);
    this.isCopySequence = true;
    setTimeout(() => {
      this.isCopySequence = false;
    }, 3000);

    this.gaService.eventEmitter('sequence_copy', 'interaction', 'click', "copy_sequence", "Click on Copy sequence");
  }

  public getQuality(qscore: number, assignment: string): string {
    if (qscore >= 75 && assignment.includes("C.A.T.H. Homologous Superfamily")) {
        return "High-confidence"; //2 stars
    } 
    if (qscore >= 70 && (assignment.includes("C.A.T.H. Homologous") || assignment.includes("C.A.T. Fold"))) {
        return "Moderate"; //1 star
    }
    return "Uncertain"; // No star/Empty star
  }

  private getBoundaries(similarTeds): string {
    const mappedSimilarTeds = similarTeds.map(
      (item) => item.start + '-' + item.end
    );
    return mappedSimilarTeds.join(', ');
  }

  private getTotalLength(similarTeds) {
    return similarTeds.reduce(
      (acc, current) => acc + (current.end - current.start) + 1,
      0
    );
  }

  async clearSeqSelection(): Promise<void> {
    // const selectedSeqElements = this.el.nativeElement.querySelectorAll(
    //   '.ms-pae-selection, .ms-pae-select-green, .ms-pae-select-orange, .ms-pae-select-yellow'
    // );
    // if (selectedSeqElements) {
    //   selectedSeqElements.forEach((selEle) => {
    //     selEle.classList.remove('ms-pae-selection');
    //     selEle.classList.remove('ms-pae-select-green');
    //     selEle.classList.remove('ms-pae-select-orange');
    //     selEle.classList.remove('ms-pae-select-yellow');
    //   });
    // }
    await this.pdbeMolstar.visual.clearSequenceColor();
  }

  // applySeqSelection(selectionData) {
  //   this.clearSeqSelection();
  //   selectionData.forEach((ele) => {
  //     for (
  //       let seqid = ele.start_residue_number;
  //       seqid <= ele.end_residue_number;
  //       seqid++
  //     ) {
  //       const seqElement = this.el.nativeElement.querySelector(
  //         `.msp-sequence-present[data-seqid="${seqid}"`
  //       );
  //       if (seqElement) {
  //         seqElement.classList.add(`${ele.class}`);
  //       }
  //     }
  //   });
  // }

  validateBlankSelection(regions) {
    regions.forEach((select) => {
      if (select.start === 0 && select.end === 0) {
        this.clearSeqSelection();
        this.pdbeMolstar.visual.reset({ camera: true });
      }
    });
    return;
  }

  validateRegions = (regions) => {
    let valid = true;
    for (const region of regions) {
      if (region.start === 0 && region.end === 0) {
        valid = false;
        return;
      }
    }
    return valid;
  };

  applyMolstarColoring(kind: 'plddt' | 'missense' | 'ted') {
    if (kind === 'missense') {
      if (this.rangeFilter) {
        colorByAlphaMissense(
          this.pdbeMolstar,
          this.csvAmData(),
          this.rangeFilter
        );
      } else {
        colorByAlphaMissense(this.pdbeMolstar, this.csvAmData());
      }
      applyAlphaMissenseTooltips(this.pdbeMolstar, this.csvAmData());
      this.gaService.eventEmitter(
        'AM_toggle',
        'AlphaMissense',
        'click',
        'AM_toggle',
        'Toggle between AM and pLDDT colouring on Mol*'
      );
    } else if (kind === 'plddt') {
      resetColoring(this.pdbeMolstar);
      resetTooltips(this.pdbeMolstar);
      this.pdbeMolstar.visual.reset({ camera: true });
    } else if (kind === 'ted') {
      // colorByTed(this.pdbeMolstar, this.tedDomainData().filter(domain=>domain.ted_domain_no=== this.activeDomain().tedNumber));
      colorByTed(this.pdbeMolstar, this.tedDomainData());
     
      this.gaService.eventEmitter(
        'domains_toggle',
        'domains',
        'click',
        'domains_toggle',
        'Toggle between TED colouring on Mol*'
      );
    }  else {
      console.error('Unknow coloring kind:', kind);
    }
  }

  cmSelectionUpdate(cmSelectedRegions): void {
    this.handleCmUpdate(cmSelectedRegions);
  }

  async handleCmUpdate(regions): Promise<void> {
    if (!this.molLoadComplete) {
      return;
    }

    const validRegions = this.validateRegions(regions);

    if (!validRegions) {
      this.clearSeqSelection();
      await this.applyMolstarColoring(this.isAlphaMis ? 'missense' : 'plddt');

      this.pdbeMolstar.visual.reset({ camera: true });
      return;
    }

    let adjustedRegions = [];
    for (const region of regions) {
      if (region.start === 0) {
        region.start = 1;
      }
      if (region.end === 0) {
        region.end = 1;
      }
      adjustedRegions.push(region);
    }

    const selectionData = this.utilService.getSelectionData(adjustedRegions);

    await this.pdbeMolstar.visual.select({
      data: selectionData,
      nonSelectedColor: '#707372',
      keepRepresentations: true,
    });
    await this.pdbeMolstar.visual.sequenceColor({
      data: selectionData,
    });

    // this.applySeqSelection(selectionData);
  }

  initMolstar(){
    this.structure = {
      model_url: this.entryData().bcifUrl,
      model_format: 'cif',
      binary: true,
    };
    // Set last updated date
    const modelCreatedDate = new Date(this.entryData().modelCreatedDate);
    this.lastUpdateDate = `${modelCreatedDate.getDate()} ${modelCreatedDate.toLocaleString(
      'default',
      { month: 'long' }
    )} ${modelCreatedDate.getFullYear()}`;
    setTimeout(() => {
      this.handleMolstar(this.structure);
    }, 10);
  }

  async handleMolstar(structure: Structure): Promise<void> {

    const options = {
      customData: {
        url: structure.model_url,
        format: structure.model_format,
        binary: structure.binary,
      },
      subscribeEvents: false,
      bgColor: { r: 255, g: 255, b: 255 },
      alphafoldView: true,
      reactive: true,
      sequencePanel: true,
      hideCanvasControls: ['animation'],
      landscape: true,
      rightPanel: true,
      hideControls: false,
      validationAnnotation: true,
      granularity: 'residue'
      
    };

    if (this.pdbeMolstar) {
      this.pdbeMolstar.visual.update(options, true);
      this.applyMolstarColoring('ted');
    } else {
      const ele = this.el.nativeElement.querySelector('.domains-molstar-container');
      const eleSeq = this.el.nativeElement.querySelector('.domains-sequencepanel');
      
      if (ele) {
        await this.molstarPluginService.loadPlugin();
        const pluginInstance = this.molstarPluginService.createInstance();
        this.pdbeMolstar = pluginInstance;
        const layout = [
          {target: ele, component:this.molstarPluginService.getClass().UIComponents.FullLayoutNoControlsUnlessExpanded },
          {target: eleSeq, component:this.molstarPluginService.getClass().UIComponents.SequenceView },
        ]
        this.pdbeMolstar.render(layout, options);
        
        this.pdbeMolstar.events.loadComplete.subscribe((e) => {
          this.molLoadComplete = true;
          this.applyMolstarColoring('ted');
        });
      }
    }
  }
}
